import { useState, useCallback } from 'react'
import { useAuth } from './useAuth'
import { virtualTradingService } from '../services/virtualTradingService'
import { marketDataService } from '../services/marketDataService'
import { VirtualOrder, StockQuote } from '../types/investment'

interface TradingState {
  orderType: 'buy' | 'sell'
  quantity: number
  selectedStock: StockQuote | null
  orderPreview: {
    totalAmount: number
    availableCash: number
    availableShares: number
  } | null
}

interface UseTradingSimulationReturn {
  tradingState: TradingState
  orderHistory: VirtualOrder[]
  loading: boolean
  error: string | null
  submitting: boolean
  setOrderType: (type: 'buy' | 'sell') => void
  setQuantity: (quantity: number) => void
  setSelectedStock: (stock: StockQuote | null) => void
  calculateOrderPreview: () => Promise<void>
  executeOrder: () => Promise<VirtualOrder | null>
  fetchOrderHistory: () => Promise<void>
  resetTradingState: () => void
  marketHours: { isOpen: boolean; nextOpen?: string; nextClose?: string }
}

export const useTradingSimulation = (): UseTradingSimulationReturn => {
  const { user } = useAuth()
  const [tradingState, setTradingState] = useState<TradingState>({
    orderType: 'buy',
    quantity: 1,
    selectedStock: null,
    orderPreview: null
  })
  const [orderHistory, setOrderHistory] = useState<VirtualOrder[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const setOrderType = useCallback((type: 'buy' | 'sell') => {
    setTradingState(prev => ({
      ...prev,
      orderType: type,
      orderPreview: null // Clear preview when changing order type
    }))
    setError(null)
  }, [])

  const setQuantity = useCallback((quantity: number) => {
    setTradingState(prev => ({
      ...prev,
      quantity: Math.max(1, Math.floor(quantity)), // Ensure positive integer
      orderPreview: null // Clear preview when changing quantity
    }))
    setError(null)
  }, [])

  const setSelectedStock = useCallback((stock: StockQuote | null) => {
    setTradingState(prev => ({
      ...prev,
      selectedStock: stock,
      orderPreview: null // Clear preview when changing stock
    }))
    setError(null)
  }, [])

  const calculateOrderPreview = useCallback(async () => {
    console.log('🔄 Calculating order preview...', {
      user: !!user,
      selectedStock: tradingState.selectedStock,
      orderType: tradingState.orderType,
      quantity: tradingState.quantity
    })
    
    if (!user || !tradingState.selectedStock) {
      console.log('❌ Cannot calculate preview: missing user or stock')
      setTradingState(prev => ({ ...prev, orderPreview: null }))
      return
    }

    // Don't recalculate if already loading
    if (loading) {
      console.log('⏳ Already loading, skipping calculation')
      return
    }

    try {
      console.log('🔄 Setting loading to true')
      setLoading(true)
      setError(null)

      const { orderType, quantity, selectedStock } = tradingState

      if (orderType === 'buy') {
        // Get current portfolio for available cash
        console.log('💰 Getting portfolio for buy order...')
        const portfolio = await virtualTradingService.getPortfolio(user.id)
        console.log('📊 Portfolio data:', portfolio)
        const availableCash = portfolio?.currentCash || 0
        const totalAmount = quantity * selectedStock.currentPrice

        console.log('💰 Buy order preview:', {
          availableCash,
          totalAmount,
          price: selectedStock.currentPrice,
          quantity,
          hasPortfolio: !!portfolio,
          portfolioMode: portfolio?.mode
        })

        setTradingState(prev => ({
          ...prev,
          orderPreview: {
            totalAmount,
            availableCash,
            availableShares: 0 // Not applicable for buy orders
          }
        }))
      } else {
        // Get current holdings for available shares
        console.log('📈 Getting holdings for sell order...')
        const holdings = await virtualTradingService.getHoldings(user.id)
        const holding = holdings.find(h => h.symbol === selectedStock.symbol)
        const availableShares = holding?.shares || 0
        const totalAmount = quantity * selectedStock.currentPrice

        console.log('📈 Sell order preview:', {
          availableShares,
          totalAmount,
          price: selectedStock.currentPrice,
          quantity
        })

        setTradingState(prev => ({
          ...prev,
          orderPreview: {
            totalAmount,
            availableCash: 0, // Not applicable for sell orders
            availableShares
          }
        }))
      }
    } catch (err) {
      console.error('❌ Error calculating order preview:', err)
      setError(err instanceof Error ? err.message : 'Failed to calculate order preview')
    } finally {
      console.log('🏁 Setting loading to false')
      setLoading(false)
    }
  }, [user, tradingState.selectedStock, tradingState.quantity, tradingState.orderType, loading])

  const executeOrder = useCallback(async (): Promise<VirtualOrder | null> => {
    console.log('🚀 Executing order...', {
      user: !!user,
      selectedStock: tradingState.selectedStock,
      orderPreview: tradingState.orderPreview,
      orderType: tradingState.orderType,
      quantity: tradingState.quantity
    })
    
    if (!user || !tradingState.selectedStock || !tradingState.orderPreview) {
      console.log('❌ Cannot execute order: missing required information')
      setError('Missing required information to execute order')
      return null
    }

    const { orderType, quantity, selectedStock } = tradingState

    try {
      setSubmitting(true)
      setError(null)

      let order: VirtualOrder

      if (orderType === 'buy') {
        console.log('💰 Executing buy order...')
        // Validate sufficient cash
        if (tradingState.orderPreview.totalAmount > tradingState.orderPreview.availableCash) {
          throw new Error('Insufficient cash for this order')
        }

        order = await virtualTradingService.executeBuyOrder(
          user.id,
          selectedStock.symbol,
          quantity
        )
        console.log('✅ Buy order executed successfully:', order)
      } else {
        console.log('📈 Executing sell order...')
        // Validate sufficient shares
        if (quantity > tradingState.orderPreview.availableShares) {
          throw new Error('Insufficient shares for this order')
        }

        order = await virtualTradingService.executeSellOrder(
          user.id,
          selectedStock.symbol,
          quantity
        )
        console.log('✅ Sell order executed successfully:', order)
      }

      // Refresh order history
      await fetchOrderHistory()

      // Reset trading state
      resetTradingState()

      return order
    } catch (err) {
      console.error('❌ Error executing order:', err)
      setError(err instanceof Error ? err.message : 'Failed to execute order')
      return null
    } finally {
      setSubmitting(false)
    }
  }, [user, tradingState])

  const fetchOrderHistory = useCallback(async () => {
    if (!user) return

    try {
      setLoading(true)
      setError(null)
      const history = await virtualTradingService.getOrderHistory(user.id)
      setOrderHistory(history)
    } catch (err) {
      console.error('Error fetching order history:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch order history')
    } finally {
      setLoading(false)
    }
  }, [user])

  const resetTradingState = useCallback(() => {
    setTradingState({
      orderType: 'buy',
      quantity: 1,
      selectedStock: null,
      orderPreview: null
    })
    setError(null)
  }, [])

  // Get market hours
  const marketHours = marketDataService.getMarketHours()

  return {
    tradingState,
    orderHistory,
    loading,
    error,
    submitting,
    setOrderType,
    setQuantity,
    setSelectedStock,
    calculateOrderPreview,
    executeOrder,
    fetchOrderHistory,
    resetTradingState,
    marketHours
  }
}
